import type { FieldDefinition, FieldOption, QuestionnaireFieldType } from '@klaro7/shared-types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface FieldEditorProps {
  field: FieldDefinition;
  index: number;
  totalFields: number;
  onChange: (updated: FieldDefinition) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const FIELD_TYPES: { value: QuestionnaireFieldType; label: string }[] = [
  { value: 'text', label: 'Testo' },
  { value: 'email', label: 'Email' },
  { value: 'tel', label: 'Telefono' },
  { value: 'select', label: 'Selezione' },
  { value: 'radio', label: 'Radio' },
  { value: 'checkbox-group', label: 'Checkbox' },
  { value: 'textarea', label: 'Area di testo' },
  { value: 'boolean', label: 'Booleano' },
  { value: 'range', label: 'Range' },
  { value: 'lingue', label: 'Lingue' },
];

const TYPES_WITH_OPTIONS: QuestionnaireFieldType[] = ['select', 'radio', 'checkbox-group'];

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function OptionsEditor({
  options,
  onChange,
}: {
  options: FieldOption[];
  onChange: (updated: FieldOption[]) => void;
}) {
  const addOption = () => {
    onChange([...options, { value: '', label: '' }]);
  };

  const removeOption = (idx: number) => {
    onChange(options.filter((_, i) => i !== idx));
  };

  const updateOption = (idx: number, key: 'value' | 'label', val: string) => {
    const updated = options.map((item, i) =>
      i === idx ? { ...item, [key]: val } : item,
    );
    onChange(updated);
  };

  const moveOption = (idx: number, direction: -1 | 1) => {
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= options.length) return;
    const updated = [...options];
    const temp = updated[idx]!;
    updated[idx] = updated[newIdx]!;
    updated[newIdx] = temp;
    onChange(updated);
  };

  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wider">Opzioni</p>
      {options.map((opt, idx) => (
        <div key={idx} className="flex items-center gap-2">
          <input
            type="text"
            value={opt.value}
            onChange={(e) => updateOption(idx, 'value', e.target.value)}
            placeholder="Valore"
            className="w-1/3 h-8 rounded border border-neutral-300 px-2 text-xs text-neutral-900 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
          <input
            type="text"
            value={opt.label}
            onChange={(e) => updateOption(idx, 'label', e.target.value)}
            placeholder="Etichetta"
            className="flex-1 h-8 rounded border border-neutral-300 px-2 text-xs text-neutral-900 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
          <button
            type="button"
            onClick={() => moveOption(idx, -1)}
            disabled={idx === 0}
            className="h-7 w-7 flex items-center justify-center rounded text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 disabled:opacity-30 disabled:cursor-not-allowed"
            title="Sposta su"
          >
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => moveOption(idx, 1)}
            disabled={idx === options.length - 1}
            className="h-7 w-7 flex items-center justify-center rounded text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 disabled:opacity-30 disabled:cursor-not-allowed"
            title="Sposta giu"
          >
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => removeOption(idx)}
            className="h-7 w-7 flex items-center justify-center rounded text-red-400 hover:text-red-600 hover:bg-red-50"
            title="Rimuovi opzione"
          >
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={addOption}
        className="text-xs text-primary-600 hover:text-primary-700 font-medium"
      >
        + Aggiungi opzione
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function FieldEditor({
  field,
  index,
  totalFields,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
}: FieldEditorProps) {
  const updateField = (partial: Partial<FieldDefinition>) => {
    onChange({ ...field, ...partial });
  };

  const updateValidation = (key: string, value: unknown) => {
    const currentValidation = field.validation || {};
    onChange({
      ...field,
      validation: { ...currentValidation, [key]: value },
    });
  };

  const hasOptions = TYPES_WITH_OPTIONS.includes(field.type);
  const showTextValidation = field.type === 'textarea' || field.type === 'text';
  const showRangeValidation = field.type === 'range';
  const showPatternValidation = field.type === 'text';
  const showBooleanValidation = field.type === 'boolean';

  return (
    <div className="rounded-lg border border-neutral-200 bg-white overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 bg-neutral-50 border-b border-neutral-200">
        <span className="text-xs font-mono bg-neutral-200 text-neutral-600 px-1.5 py-0.5 rounded">
          {field.key}
        </span>
        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            onClick={onMoveUp}
            disabled={index === 0}
            className="h-7 w-7 flex items-center justify-center rounded text-neutral-400 hover:text-neutral-600 hover:bg-neutral-200 disabled:opacity-30 disabled:cursor-not-allowed"
            title="Sposta su"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
            </svg>
          </button>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={index === totalFields - 1}
            className="h-7 w-7 flex items-center justify-center rounded text-neutral-400 hover:text-neutral-600 hover:bg-neutral-200 disabled:opacity-30 disabled:cursor-not-allowed"
            title="Sposta giu"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          <button
            type="button"
            onClick={onRemove}
            className="h-7 w-7 flex items-center justify-center rounded text-red-400 hover:text-red-600 hover:bg-red-50"
            title="Rimuovi campo"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="px-4 py-3 space-y-3">
        {/* Label */}
        <div>
          <label className="block text-xs font-medium text-neutral-500 mb-1">Etichetta</label>
          <input
            type="text"
            value={field.label}
            onChange={(e) => updateField({ label: e.target.value })}
            className="w-full h-8 rounded border border-neutral-300 px-2 text-sm text-neutral-900 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>

        {/* Type + Required row */}
        <div className="flex gap-3 items-end">
          <div className="flex-1">
            <label className="block text-xs font-medium text-neutral-500 mb-1">Tipo</label>
            <select
              value={field.type}
              onChange={(e) => {
                const newType = e.target.value as QuestionnaireFieldType;
                const updated: Partial<FieldDefinition> = { type: newType };
                // Add default options when switching to a type that requires them
                if (TYPES_WITH_OPTIONS.includes(newType) && (!field.options || field.options.length === 0)) {
                  updated.options = [{ value: '', label: '' }];
                }
                updateField(updated);
              }}
              className="w-full h-8 rounded border border-neutral-300 px-2 text-sm text-neutral-900 bg-white focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              {FIELD_TYPES.map((ft) => (
                <option key={ft.value} value={ft.value}>
                  {ft.label}
                </option>
              ))}
            </select>
          </div>

          <label className="flex items-center gap-2 text-sm cursor-pointer pb-1">
            <input
              type="checkbox"
              checked={field.required}
              onChange={(e) => updateField({ required: e.target.checked })}
              className="h-4 w-4 rounded text-primary-600 border-neutral-300 focus:ring-primary-500"
            />
            <span className="text-xs font-medium text-neutral-600">Obbligatorio</span>
          </label>
        </div>

        {/* Placeholder */}
        <div>
          <label className="block text-xs font-medium text-neutral-500 mb-1">Placeholder</label>
          <input
            type="text"
            value={field.placeholder || ''}
            onChange={(e) => updateField({ placeholder: e.target.value || undefined })}
            className="w-full h-8 rounded border border-neutral-300 px-2 text-sm text-neutral-900 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>

        {/* Helper text */}
        <div>
          <label className="block text-xs font-medium text-neutral-500 mb-1">Testo di aiuto</label>
          <input
            type="text"
            value={field.helperText || ''}
            onChange={(e) => updateField({ helperText: e.target.value || undefined })}
            className="w-full h-8 rounded border border-neutral-300 px-2 text-sm text-neutral-900 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>

        {/* Options section */}
        {hasOptions && (
          <OptionsEditor
            options={field.options || []}
            onChange={(opts) => updateField({ options: opts })}
          />
        )}

        {/* Validation: text / textarea */}
        {showTextValidation && (
          <div className="space-y-2 border-t border-neutral-100 pt-3">
            <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wider">Validazione</p>
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="block text-xs font-medium text-neutral-500 mb-1">Min caratteri</label>
                <input
                  type="number"
                  value={field.validation?.minLength ?? ''}
                  onChange={(e) => updateValidation('minLength', e.target.value ? Number(e.target.value) : undefined)}
                  className="w-full h-8 rounded border border-neutral-300 px-2 text-xs text-neutral-900 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>
              <div className="flex-1">
                <label className="block text-xs font-medium text-neutral-500 mb-1">Max caratteri</label>
                <input
                  type="number"
                  value={field.validation?.maxLength ?? ''}
                  onChange={(e) => updateValidation('maxLength', e.target.value ? Number(e.target.value) : undefined)}
                  className="w-full h-8 rounded border border-neutral-300 px-2 text-xs text-neutral-900 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>
            </div>
          </div>
        )}

        {/* Validation: pattern + isUrl (text only) */}
        {showPatternValidation && (
          <div className="space-y-2">
            <div>
              <label className="block text-xs font-medium text-neutral-500 mb-1">Pattern (regex)</label>
              <input
                type="text"
                value={field.validation?.pattern ?? ''}
                onChange={(e) => updateValidation('pattern', e.target.value || undefined)}
                placeholder="es. ^[A-Z].*"
                className="w-full h-8 rounded border border-neutral-300 px-2 text-xs text-neutral-900 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={field.validation?.isUrl ?? false}
                onChange={(e) => updateValidation('isUrl', e.target.checked || undefined)}
                className="h-3.5 w-3.5 rounded text-primary-600 border-neutral-300 focus:ring-primary-500"
              />
              <span className="text-neutral-600">Valida come URL</span>
            </label>
          </div>
        )}

        {/* Validation: range */}
        {showRangeValidation && (
          <div className="space-y-2 border-t border-neutral-100 pt-3">
            <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wider">Validazione</p>
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="block text-xs font-medium text-neutral-500 mb-1">Min</label>
                <input
                  type="number"
                  value={field.validation?.min ?? ''}
                  onChange={(e) => updateValidation('min', e.target.value ? Number(e.target.value) : undefined)}
                  className="w-full h-8 rounded border border-neutral-300 px-2 text-xs text-neutral-900 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>
              <div className="flex-1">
                <label className="block text-xs font-medium text-neutral-500 mb-1">Max</label>
                <input
                  type="number"
                  value={field.validation?.max ?? ''}
                  onChange={(e) => updateValidation('max', e.target.value ? Number(e.target.value) : undefined)}
                  className="w-full h-8 rounded border border-neutral-300 px-2 text-xs text-neutral-900 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>
            </div>
          </div>
        )}

        {/* Validation: boolean (isLiteral) */}
        {showBooleanValidation && (
          <div className="space-y-2 border-t border-neutral-100 pt-3">
            <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wider">Validazione</p>
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={field.validation?.isLiteral ?? false}
                onChange={(e) => updateValidation('isLiteral', e.target.checked || undefined)}
                className="h-3.5 w-3.5 rounded text-primary-600 border-neutral-300 focus:ring-primary-500"
              />
              <span className="text-neutral-600">Deve essere true (letterale)</span>
            </label>
            {field.validation?.isLiteral && (
              <div>
                <label className="block text-xs font-medium text-neutral-500 mb-1">Messaggio errore</label>
                <input
                  type="text"
                  value={field.validation?.literalMessage ?? ''}
                  onChange={(e) => updateValidation('literalMessage', e.target.value || undefined)}
                  placeholder="es. Devi accettare"
                  className="w-full h-8 rounded border border-neutral-300 px-2 text-xs text-neutral-900 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
